
import { supabase } from '@/integrations/supabase/client';

interface EmailSettings {
    service_id: string;
    template_id: string;
    public_key: string;
    to_emails: string[];
    cc_emails: string[];
    is_active: boolean;
}

/**
 * Sends an email notification using EmailJS based on settings stored in Supabase.
 * 
 * Requires the EmailJS template to use variables:
 * - {{subject}}
 * - {{title}}
 * - {{message_html}}
 * - {{to_email}} (Configure 'To Email' in EmailJS template settings to use this variable)
 * - {{cc_email}} (Configure 'CC' in EmailJS template settings to use this variable)
 */
// Helper to generate a beautiful HTML email wrapper
const generateEmailTemplate = (title: string, contentHtml: string, actionUrl?: string, actionText?: string) => {
    const buttonHtml = actionUrl && actionText ? `
      <div class="text-center mt-4">
        <a href="${actionUrl}" class="button">${actionText}</a>
      </div>
    ` : '';

    return `
<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<style>
  /* Reset */
  body { margin: 0; padding: 0; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; background-color: #f3f4f6; -webkit-font-smoothing: antialiased; }
  table { border-collapse: collapse; border-spacing: 0; }
  
  /* Layout */
  .wrapper { width: 100%; background-color: #f3f4f6; padding: 40px 0; }
  .container { max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 10px 25px rgba(0,0,0,0.05); }
  
  /* Header */
  .header { background-color: #000000; padding: 40px 20px; text-align: center; background-image: linear-gradient(45deg, #000000 0%, #1a1a1a 100%); }
  .logo { color: #D4AF37; font-size: 24px; font-weight: 700; letter-spacing: 4px; text-transform: uppercase; margin: 0; text-decoration: none; }
  
  /* Body */
  .body { padding: 40px 30px; }
  .title { font-size: 22px; color: #111; margin: 0 0 20px 0; font-weight: 600; text-align: center; }
  
  /* Components */
  .info-box { background-color: #f9f9f9; border-radius: 8px; padding: 20px; margin-bottom: 20px; border-left: 4px solid #D4AF37; }
  .info-row { margin-bottom: 10px; }
  .info-label { color: #888; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; display: block; margin-bottom: 4px; }
  .info-value { color: #111; font-size: 15px; font-weight: 500; }
  
  .section-title { font-size: 16px; font-weight: 600; color: #111; margin: 25px 0 15px 0; padding-bottom: 8px; border-bottom: 1px solid #eee; }
  
  .highlight { color: #D4AF37; font-weight: bold; }
  
  /* Review Stars */
  .stars { color: #D4AF37; font-size: 18px; letter-spacing: 2px; }
  
  /* Button */
  .button { display: inline-block; background-color: #000000; color: #D4AF37; padding: 14px 28px; text-decoration: none; font-weight: bold; border-radius: 6px; font-size: 14px; text-transform: uppercase; letter-spacing: 1px; transition: background 0.3s; }
  
  /* Footer */
  .footer { background-color: #1a1a1a; padding: 30px; text-align: center; border-top: 1px solid #222; }
  .footer-text { color: #666; font-size: 12px; margin-bottom: 10px; }
  .copyright { color: #444; font-size: 11px; }
  
  /* Utilities */
  .text-center { text-align: center; }
  .mt-4 { margin-top: 25px; }
  
  @media only screen and (max-width: 600px) {
    .body { padding: 25px 20px; }
    .header { padding: 30px 15px; }
    .button { display: block; width: auto; }
  }
</style>
</head>
<body>
  <div class="wrapper">
    <div class="container">
      <!-- Header -->
      <div class="header">
        <div class="logo">VISTA PHOTOGRAPHY</div>
        <div style="color: #666; font-size: 10px; letter-spacing: 2px; margin-top: 8px; text-transform: uppercase;">Premium Notification System</div>
      </div>
      
      <!-- Content -->
      <div class="body">
        <h1 class="title">${title}</h1>
        ${contentHtml}
        ${buttonHtml}
      </div>
      
      <!-- Footer -->
      <div class="footer">
         <div class="copyright">&copy; ${new Date().getFullYear()} Vista Photography. All rights reserved.</div>
      </div>
    </div>
  </div>
</body>
</html>
    `;
};

export const sendNotification = async (
    subject: string,
    title: string,
    messageHtml: string,
    replyTo?: string,
    actionUrl?: string,
    actionText?: string
) => {
    try {
        // 1. Fetch settings
        const { data, error } = await supabase
            .from('notification_settings' as any)
            .select('*')
            // We fetch without .eq filter to avoid RLS issues if is_active column is restricted
            .limit(1)
            .maybeSingle();

        if (error) {
            console.error('Error fetching email settings:', error);
            return;
        }

        if (!data) {
            console.log('Email notifications are disabled or not configured.');
            return;
        }

        const settings = data as unknown as EmailSettings;

        if (!settings.service_id) {
            console.warn('Google Script URL missing in settings.');
            return;
        }

        if (!settings.is_active) {
            console.log('Email notifications are currently disabled via toggle.');
            return;
        }

        // 2. Wrap Content in Beautiful Template with Action Button
        const fullHtml = generateEmailTemplate(title, messageHtml, actionUrl, actionText);

        // 3. Prepare parameters
        const templateParams = {
            subject: subject,
            to_email: settings.to_emails.length > 0 ? settings.to_emails.join(',') : undefined,
            cc_email: settings.cc_emails.length > 0 ? settings.cc_emails.join(',') : undefined,
            message_html: fullHtml, // Send the full template
            reply_to: replyTo
        };

        // 4. Send using Google Apps Script (Gmail)
        // We use mode: 'no-cors' and text/plain to avoid CORS preflight (OPTIONS) requests which GAS fails on.
        // This ensures the request actually reaches the script.
        await fetch(settings.service_id, {
            method: 'POST',
            mode: 'no-cors',
            headers: {
                'Content-Type': 'text/plain', // Important: effectively bypasses preflight
            },
            body: JSON.stringify(templateParams)
        });

        console.log('Notification request sent to Google Script (Opaque).');
    } catch (error) {
        console.error('Failed to send email notification:', error);
    }
};
